import type { NoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import InteractivePrototype from './interactivePrototype';
import type { InteractiveOptions } from './interactivePrototype';

import utils from '../utils';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import Note from '../uTheory.Notation/note';
import Chord from '../uTheory.Notation/chord';
import type { AnswerAndData } from './interactiveQuestionPrototype';

interface ChordSpellingOptions extends InteractiveOptions {
  triadOrSeventh: 'triads' | 'sevenths';
}

interface ChordSpellingQuestion {
  question: string;
  answer: string;
  chord: Chord;
  text: string[];
  skills: string[];
}

export default class ChordSpelling extends InteractivePrototype {
  answer: string;

  declare options: ChordSpellingOptions;

  answerDiv: HTMLDivElement;

  declare questions: ChordSpellingQuestion[];

  // Public constructor
  constructor(displayDiv: HTMLDivElement, options: ChordSpellingOptions) {
    super(displayDiv, options);
    this.targetSpeedFromSkills =
      this.options.triadOrSeventh !== 'sevenths' ? ['spelling_triads'] : ['seventh_chord_spelling'];
    this.settings = {
      instructions: {
        ariaLive: 'alert',
        ariaLiveRegion: 0,
      },
      notation: false,
      letters: {
        letterButtons: true,
        trashButton: true,
        checkButton: true,
      },
      checkMark: {},
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };
    this.layout = {
      instructions: {
        maxPercent: 40,
        noWrap: true,
      },
    };
    this.init(displayDiv, options);
  }

  init(displayDiv: HTMLDivElement, options: ChordSpellingOptions): Promise<any> {
    this.answer = '';
    return super.init(displayDiv, options);
  }

  generateQuestions(): void {
    const options = this.options;
    const drill = options.drill !== undefined && options.drill;
    this.questions = drill
      ? this.generateDrillQuestions(options)
      : this.generateManualQuestions(options);
  }

  generateManualQuestions(options: ChordSpellingOptions): ChordSpellingQuestion[] {
    if (!utils.isArray(options.questions))
      throw new Error(
        'An array must be provided in options.questions for non-drill initialization.'
      );
    return this.processQuestions(options.questions, options);
  }

  generateDrillQuestions(options: ChordSpellingOptions): ChordSpellingQuestion[] {
    let questions = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];

    const randomOctave = () => [2, 3, 4][Math.floor(Math.random() * 3)];
    const qg = new QuestionGenerator();
    questions = qg
      .rejectDuplicates(questions, this.options.maxQuestions || 5)
      .map((question) => `${question}${randomOctave()}`);

    return this.processQuestions(questions, options);
  }

  processQuestions(questions: string[], options: ChordSpellingOptions): ChordSpellingQuestion[] {
    return questions.map((question, index) => {
      const root = Note.fromStringOrNote(question);
      const quality =
        options.triadOrSeventh === 'triads'
          ? 'M'
          : options.triadOrSeventh === 'sevenths'
          ? 'M7'
          : 'M';
      const chord = new Chord(root, quality);
      let answer = chord.root.letter + chord.third.letter + chord.fifth.letter;

      if (options.triadOrSeventh === 'sevenths') {
        answer += chord.seventh.letter;
      }

      const triadOrSeventh = chord.isSeventh === true ? 'seventh chord' : 'triad';
      const questionText = `${triadOrSeventh} on ${root.letter}`;

      return {
        question: root.letter,
        answer,
        chord,
        text: [questionText, `${index + 1}`],
        skills:
          this.options.triadOrSeventh !== 'sevenths'
            ? ['spelling_triads']
            : ['seventh_chord_spelling'],
      };
    });
  }

  newQuestion(): void {
    this.questionPrompt = this.getQuestionPrompt();
    this.progressTracker.start(this.targetSpeed);
    super.newQuestion();
  }

  // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-empty-function
  initializeMusic(): void {}

  getQuestionPrompt(): string {
    if (this.options.testMode) {
      return 'Spell a {question}.';
    }
    return `Spell a {question}.<br/><small>Question #{question} of ${this.questions.length}</small>`;
  }

  newCheckpointQuestion(questionString): Promise<any> {
    return this.loadingPromise.then(() => {
      const root = `${questionString}4` as NoteName;
      const quality =
        this.options.triadOrSeventh === 'triads'
          ? 'M'
          : this.options.triadOrSeventh === 'sevenths'
          ? 'M7'
          : 'M';
      const chord = new Chord(root, quality, 0);
      let answer = chord.root.letter + chord.third.letter + chord.fifth.letter;
      const questions = [];

      if (this.options.triadOrSeventh === 'sevenths') {
        answer += chord.seventh.letter;
      }

      questions.push({
        question: questionString,
        answer,
        chord,
      });

      this.questions = questions;

      this.newQuestion();
      this.resize();
      return Promise.resolve(this.currentQuestion);
    });
  }

  createCustomDivs(): void {
    const div = document.createElement('div');
    const index = 1; // because we know we need to insert before the 2nd
    // element in displayDiv

    this.answerDiv = div;
    const styleOptions = {
      fontSize: '4vw',
      fontWeight: 'bold',
      textAlign: 'center',
      padding: '10px',
    };

    div.id = 'answerDiv';
    div.classList.add('accidentals');
    div.innerHTML = '?';

    utils.merge(div.style, styleOptions);

    this.displayDiv.insertBefore(div, this.displayDiv.children[index]);
  }

  addListeners(): void {
    this.letterPanel.addLetterCallback(this.addLetterToAnswer, this);
    this.letterPanel.addCustomButtonCallback(this.addLetterToAnswer, this);
    this.letterPanel.addCheckCallback(this.checkAnswer, this);
  }

  addLetterToAnswer(letter: string): void {
    this.addAnswerValue(letter);
    return this.displayAnswer();
  }

  addAnswerValue(value: string): void {
    const triadOrSeventh =
      this.options.triadOrSeventh === 'triads'
        ? 3
        : this.options.triadOrSeventh === 'sevenths'
        ? 4
        : 3;
    if (value === 'trash') {
      this.answer = this.answer.slice(0, -1);
    } else {
      this.answer = this.answer.length < triadOrSeventh ? (this.answer += value) : this.answer;
    }
  }

  displayAnswer(): void {
    const answer = this.answer;

    if (answer === '') {
      this.answerDiv.removeAttribute('aria-live');
      this.answerDiv.innerHTML = '?';
    } else {
      this.answerDiv.setAttribute('aria-live', 'polite');
      this.answerDiv.innerHTML = answer;
    }
  }

  checkAnswer(): void {
    if (this.rightAnswerDelay) return;
    const testMode = this.optionsTrue('testMode');
    const rightAnswer = this.questions[this.questionNumber].answer;
    const userAnswer = this.answer;
    const correct = rightAnswer === userAnswer;

    if (testMode) {
      this.checkpointAnswer(correct);
      this.clearAnswer();
      return;
    }

    if (correct) {
      this.getAnswerAndData(true);
      this.rightAnswer(800);
      window.setTimeout(() => this.clearAnswer(), 800);
    } else {
      this.getAnswerAndData(false);
      this.wrongAnswer();
    }
  }

  getAnswerAndData(correct: boolean): AnswerAndData {
    const question = this.questions[this.questionNumber];
    const correctAnswer = question.answer;
    const answerAndData = {
      question: question.question,
      questionType: 'ChordSpelling',
      questionText: this.instructionsDiv.innerHTML,
      questionSVG: null,
      studentAnswerText: `${this.answer}`,
      correctAnswerText: `${correctAnswer}`,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: question.skills,
    };

    return answerAndData;
  }

  clearAnswer(): void {
    this.answer = '';
    this.answerDiv.innerHTML = '?';
  }

  playAnswer(): void {
    const chord = this.currentQuestion.chord;
    const nh = chord.noteHolder.clone();
    nh.forEach((note) => {
      note.accidental = '';
    });
    this.player.play(nh, { startTime: 0, stopTime: 1, velocity: 0.6 });
  }

  gameOver(): void {
    this.instructionsDiv.innerHTML = 'Nice work!';
    this.instructionsDiv.setAttribute('role', 'alert');
    this.displayDiv.removeChild(this.lettersDiv);
    this.displayDiv.removeChild(this.answerDiv);
    super.gameOver();
  }

  static registry = [
    {
      description: 'Spell Triads',
      skills: ['spelling_triads'],
      options: {
        triadOrSeventh: 'triads',
      },
      maxQuestions: 5,
    },
    {
      description: 'Spell Seventh Chords',
      skills: ['seventh_chord_spelling'],
      options: {
        triadOrSeventh: 'sevenths',
      },
      maxQuestions: 5,
    },
  ];
}
